import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {PipeId, NodeSpec} from '../execution/graph';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {
  ArrayValue,
  ConcentrationValue,
  DataValue,
  PlatedMaterialValue,
  StringValue,
  TableType,
  TableValue,
  VolumeValue,
} from '../types/data';

import {LABEL_COLUMN_ID, MixingTransformer, toZero} from './mixing_transformer';
import {makeWaterPlatedMaterial} from './sources';

export const NODE = {
  id: 'node_mix_product',
  label: 'Mix product',
  inputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
    {
      id: 'prefix',
      label: 'Prefix',
      type: {kind: 'string'},
    },
    {
      id: 'mix',
      label: 'Mixes',
      type: {
        kind: 'table',
        columnLabels: [],
        columnTypes: [],
        defaults: [],
        rowCountFixed: true,
      },
    },
    {
      id: 'output_plate',
      label: 'Plate',
      type: {
        kind: 'plate',
        contents: undefined,
      },
    },
    {
      id: 'remainder',
      label: 'Remainder',
      type: {
        kind: 'plated_material',
        material: undefined,
      },
    },
  ],
  outputs: [
    {
      id: 'materials',
      label: 'Materials',
      type: {
        kind: 'array',
        elementsType: {
          kind: 'plated_material',
          material: undefined,
        },
      },
    },
  ],
  pipe: ({node, overlay}) => {
    const materials = overlay.padToSources(
      checkExists(node.inputs.get('materials')),
    ) as Array<Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>>;
    const prefix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('prefix')),
      'string',
      () => ({kind: 'string', value: 'product'}),
    );
    const mix = overlay.singleValuePadSource(
      checkExists(node.inputs.get('mix')),
      'table',
      () => ({
        kind: 'table',
        values: new Map([]),
      }),
    );
    const plate = overlay.singleValuePadSource(
      checkExists(node.inputs.get('output_plate')),
      'plate',
      context => ({
        kind: 'plate',
        id: context.lab.presetPlates.fourCelsius,
        wells: new Map(),
      }),
    );
    const remainder = overlay.singleValuePadSource(
      checkExists(node.inputs.get('remainder')),
      'plated_material',
      context => makeWaterPlatedMaterial(context),
    );

    const mixes = new FlattenMixToArrayTransformer(
      overlay.nextIdFor(node),
      product,
      materials,
      mix,
      prefix,
    );
    const mixing = new MixingTransformer(
      overlay.nextIdFor(node),
      materials,
      mixes,
      plate,
      remainder,
    );
    overlay.setPadSource(checkExists(node.outputs.get('materials')), mixing);
  },
} as const satisfies NodeSpec;

export class FlattenMixToArrayTransformer extends PTransformer<
  DataValue,
  TableValue
> {
  constructor(
    id: PipeId,
    private readonly producer: (
      values: PlatedMaterialValue[][],
    ) => Generator<number[], undefined, undefined>,
    private readonly materials: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly mix: Source<TableValue>,
    private readonly prefix: Source<StringValue>,
  ) {
    super(id, [...materials, mix, prefix]);
  }

  async collate(context: PipeContext): Promise<void> {
    const materials: PlatedMaterialValue[][] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.materials.indexOf(source as any) >= 0) {
        const group: PlatedMaterialValue[] = [];
        walkMaterials(value, reject, v => {
          group.push(v);
        });
        if (group.length > 0) {
          materials.push(group);
        }
      }
    }

    if (materials.length < 1) {
      await this.gatherConstraints({kind: 'table', values: new Map()}, context);
      return;
    }

    const mixSource = checkExists(this.values.get(this.mix));
    const prefixSource = checkExists(this.values.get(this.prefix));
    const prefixValue =
      (prefixSource.value as StringValue | undefined)?.value ?? '';

    const mixValue = checkExists(mixSource.value) as TableValue;
    const [mixesType, mixesValue] = updateTable(mixValue, materials);

    const flattened = materials.flatMap(m => m);
    const columns: DataValue[][] = flattened.map(() => []);
    const labels: StringValue[] = [];
    const zero: VolumeValue = {
      kind: 'volume',
      numberNl: 0,
      preferredUnit: 'ul',
    };
    for (const entry of this.producer(materials)) {
      labels.push({
        kind: 'string',
        value: prefixValue + '.' + entry.map(i => i + 1).join('_'),
      });
      const resolved = materials.map((g, i) => g[entry[i]]);
      let offset = 0;
      for (let i = 0; i < materials.length; ++i) {
        const group = materials[i];
        const label = labelForGroup(group);
        const rawAmount = checkExists(mixesValue.values.get(label))[0];
        const syntheticX = group.length !== 1;
        for (let j = 0; j < group.length; ++j) {
          const material = group[j];
          const column = columns[offset + j];
          if (resolved[i] === material) {
            const baseAmount = material.material.amount;
            let amount: DataValue;
            // If we're pulling ingredients from a serial dilution node then we want to say "0.5x"
            // and have it mean "half the serial diluted thing, regardless of the serial diluted
            // thing's concentration relative the thing it was made from". So here we are going to
            // map it correctly.
            if (rawAmount.kind === 'concentration' && rawAmount.unit === 'x') {
              if (syntheticX && baseAmount.kind === 'concentration') {
                amount = {
                  kind: 'concentration',
                  number: rawAmount.number * baseAmount.number,
                  unit: baseAmount.unit,
                };
              } else {
                amount = rawAmount;
              }
            } else {
              amount = rawAmount;
            }
            column.push(amount);
          } else {
            column.push(zero);
          }
        }
        offset += group.length;
      }
    }

    const providing: TableValue = {
      kind: 'table',
      values: new Map(columns.map((c, i) => [flattened[i].material.label, c])),
    };
    providing.values.set(LABEL_COLUMN_ID, labels);
    try {
      await this.gatherConstraints(providing, context);
    } catch (e: unknown) {
      mixSource.reject(e);
      return;
    }

    mixSource.resolve({
      type: mixesType,
      value: {
        expected: mixesValue,
        reason: 'May have updated table',
      },
    });
  }
}

export function updateTable(
  existing: TableValue,
  materials: PlatedMaterialValue[][],
): [TableType, TableValue] {
  const groups: string[] = materials.map(m => labelForGroup(m));
  const initialAmounts: ConcentrationValue[] = materials.map(m =>
    m.length === 1
      ? m[0].material.amount.kind === 'concentration'
        ? m[0].material.amount
        : {kind: 'concentration', number: 1, unit: 'x'}
      : {kind: 'concentration', number: 1, unit: 'x'},
  );

  const type: TableType = {
    kind: 'table',
    columnLabels: groups,
    columnTypes: initialAmounts.map(() => ({kind: 'concentration'})),
    defaults: initialAmounts,
    rowCountFixed: true,
  };

  const columns = type.columnLabels;
  const rows = 1;

  let willChange = false;
  for (const column of columns) {
    const old = existing?.values?.get(column);
    if (!old || old.length !== rows) {
      willChange = true;
      break;
    }
  }
  if (!willChange && existing?.values.size === columns.length) {
    return [type, existing];
  }

  const table: TableValue = {
    kind: 'table',
    values: new Map(),
  };

  const factories = new Map<string, (i: number) => DataValue>([
    ...groups.map((m, i) => [m, () => initialAmounts[i]] as const),
  ]);
  for (const column of columns) {
    const old = existing.values?.get(column);
    let now;
    if (old) {
      now = old.slice(0, rows);
    } else {
      now = Array(rows);
    }
    table.values.set(column, now);

    const factory = checkExists(factories.get(column));
    for (let i = 0; i < rows; ++i) {
      now[i] ??= toZero(factory(i));
    }
  }

  return [type, table];
}

function labelForGroup(group: PlatedMaterialValue[]): string {
  return group[0].material.label + ' etc';
}

export function* product<V>(
  groups: V[][],
): Generator<number[], undefined, undefined> {
  const head = groups[0];
  const tail = groups.slice(1);
  for (let i = 0; i < head.length; ++i) {
    for (const r of tail.length > 0 ? product(tail) : [[]]) {
      yield [i, ...r];
    }
  }
}
